/**
 * Travel Planner Arche — API surface.
 *
 * All HTTP methods this arche needs live here, callable as
 * `travelApi.<method>(...)`. The methods use the shared transport
 * exposed by the `api` singleton (`api.httpRequest`) rather than living
 * on the singleton itself — so `client/src/lib/api.ts` no longer grows
 * with arche-specific surface area.
 *
 * Mirrors the social-media slice (see `arche/arches/social-media/api/`).
 * Method names are kept identical to the previous `api.<methodName>()`
 * shape to minimize consumer churn — callers just swap the import and
 * prefix.
 */

import { api } from '@/lib/api';

// ==========================================================================
// Travel Planner Types
// ==========================================================================

export type TravelEventType = 'sight' | 'food' | 'activity' | 'transport' | 'hotel' | 'note';
export type TravelTransitMode =
  | 'Flight'
  | 'Train'
  | 'Bus'
  | 'Drive'
  | 'Ferry'
  | 'Helicopter'
  | 'Other';

export interface TravelTripResponse {
  tripId: string;
  ownerId: string;
  title: string;
  startDate: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface TravelLocationEntry {
  city: string;
  country?: string;
}

export interface TravelItemResponse {
  itemId: string;
  tripId: string;
  itemType: 'day' | 'transit';
  sortOrder: number;
  dayNumber: number | null;
  dayTitle: string | null;
  city: string | null;
  country: string | null;
  locations: TravelLocationEntry[];
  accommodationName: string | null;
  accommodationAddress: string | null;
  accommodationCheckin: string | null;
  accommodationCheckout: string | null;
  transitFrom: string | null;
  transitTo: string | null;
  transitMode: TravelTransitMode | null;
  transitDeparture: string | null;
  transitArrival: string | null;
  transitNotes: string | null;
  lat: number | null;
  lng: number | null;
  createdAt: string;
  updatedAt: string;
}

export type TravelReservationStatus =
  | 'unknown'
  | 'no_booking'
  | 'suggested'
  | 'required'
  | 'booked';

export interface TravelReservationNotesResponse {
  bookAheadDays?: number;
  method?: 'resy' | 'opentable' | 'phone' | 'email' | 'walk-in' | 'other';
  url?: string;
  summary?: string;
  draftEmail?: string;
  sources?: Array<{ url: string; title?: string }>;
}

export type TravelReservationSuggestionCategory =
  | 'restaurant'
  | 'attraction'
  | 'excursion'
  | 'event';
export type TravelReservationSuggestionStatus = 'suggested' | 'interested' | 'booked' | 'dismissed';

export type TravelBudgetLevel = 'budget' | 'moderate' | 'comfortable' | 'luxury';
export type TravelPace = 'relaxed' | 'balanced' | 'packed';

export interface TravelTripInfoResponse {
  tripId: string;
  partySize: number | null;
  relationship: string[];
  interests: string[];
  budgetLevel: TravelBudgetLevel | null;
  hardBudgetCents: number | null;
  pace: TravelPace | null;
  dietary: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTravelTripInfoInput {
  partySize?: number | null;
  relationship?: string[];
  interests?: string[];
  budgetLevel?: TravelBudgetLevel | null;
  hardBudgetCents?: number | null;
  pace?: TravelPace | null;
  dietary?: string[];
  notes?: string | null;
}

export interface TravelReservationSuggestionResponse {
  suggestionId: string;
  tripId: string;
  category: TravelReservationSuggestionCategory;
  title: string;
  location: string | null;
  description: string | null;
  bookingMethod: string | null;
  url: string | null;
  leadTimeDays: number | null;
  duration: string | null;
  status: TravelReservationSuggestionStatus;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface TravelEventResponse {
  eventId: string;
  itemId: string;
  time: string | null;
  title: string;
  eventType: TravelEventType;
  notes: string | null;
  sortOrder: number;
  lat: number | null;
  lng: number | null;
  /** True when the geocoder found multiple plausible matches and the picked
   * lat/lng is the one closest to the parent day. The map popup uses this
   * to tell the traveller other branches exist. */
  hasAlternateLocations: boolean;
  reservationStatus: TravelReservationStatus | null;
  reservationNotes: TravelReservationNotesResponse | null;
  createdAt: string;
  updatedAt: string;
}

export interface TravelTodoResponse {
  todoId: string;
  tripId: string;
  category: string;
  title: string;
  notes: string | null;
  completed: boolean;
  sortOrder: number;
  source: 'user' | 'ai';
  createdAt: string;
  updatedAt: string;
}

export interface TravelShareLinkResponse {
  shareId: string;
  tripId: string;
  expiresAt: string | null;
  revoked: boolean;
  createdAt: string;
}

export interface TravelShareLinkWithTripResponse extends TravelShareLinkResponse {
  tripTitle: string;
}

/**
 * Shape returned from the public, unauthenticated shared-trip endpoint.
 * Mirrors TravelTripDetailResponse but strips ownerId everywhere and
 * attaches per-attachment signed download URLs + their expiry.
 */
export interface TravelSharedTripResponse {
  shareId: string;
  expiresAt: string | null;
  attachmentUrlTtlSeconds: number;
  trip: {
    tripId: string;
    title: string;
    startDate: string;
    status: string;
  };
  items: Array<Omit<TravelItemResponse, 'createdAt' | 'updatedAt'>>;
  events: Array<Omit<TravelEventResponse, 'createdAt' | 'updatedAt'>>;
  attachments: Array<
    Omit<TravelAttachmentResponse, 'createdAt'> & {
      downloadUrl: string | null;
      downloadExpiresAt: string | null;
    }
  >;
}

export interface TravelCriticSignalsResponse {
  perDayEventCounts: Array<{ dayNumber: number; count: number }>;
  gapsByDay: Array<{ dayNumber: number; gapsMinutes: number[] }>;
  transitToFirstEventMinutes: Array<{
    transitItemId: string;
    nextDayNumber: number;
    minutes: number | null;
  }>;
  totalWalkingKmByDay: Array<{ dayNumber: number; km: number }> | null;
}

export interface TravelGeocodeResult {
  lat: number;
  lng: number;
  displayName?: string;
}

export interface TravelAttachmentResponse {
  attachmentId: string;
  tripId: string;
  itemId: string | null;
  eventId: string | null;
  fileId: string;
  label: string | null;
  displayOrder: number;
  createdAt: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

export interface TravelTripDetailResponse {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
}

export interface TravelTripListResponse {
  trips: TravelTripResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface TravelerProfileFields {
  diet?: string[];
  allergies?: string[];
  mobility?: 'low' | 'moderate' | 'high';
  pace?: 'slow' | 'moderate' | 'packed';
  budget?: 'shoestring' | 'mid' | 'premium' | 'luxury';
  interests?: string[];
  dislikes?: string[];
  languages?: string[];
  homeCity?: string;
  notes?: string;
  /** Feature 4 critic: user-specified focus areas folded into the default
   * six sections. Settings → Trip Analysis edits this. */
  criticFocusAreas?: string[];
}

export interface TravelUserProfileResponse {
  profile: TravelerProfileFields;
  features: Record<string, boolean>;
  /**
   * The user's plans+<token>@<domain> forwarding address, or `null` if the
   * feature is disabled / no domain is configured server-side. Only populated
   * when the `email_inbox` feature is enabled.
   */
  inboundAddress?: string | null;
  updatedAt: string;
}

export type TravelInboundEmailStatus = 'pending' | 'ok' | 'error' | 'unassigned';

export interface TravelInboundEmailResponse {
  emailId: string;
  ownerId: string;
  receivedAt: string;
  subject: string | null;
  fromAddress: string | null;
  rawS3Key: string;
  parseStatus: TravelInboundEmailStatus;
  parseResult: {
    commands?: Array<{ tool: string; args: Record<string, unknown> }>;
    summary?: string;
    confirmationNumber?: string;
    reason?: string;
  };
  targetTripId: string | null;
  error: string | null;
  dedupeKey: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TravelInboxListResponse {
  emails: TravelInboundEmailResponse[];
  total: number;
  featureEnabled: boolean;
}

export type TravelFlightStatusValue =
  | 'unknown'
  | 'scheduled'
  | 'active'
  | 'landed'
  | 'diverted'
  | 'delayed'
  | 'cancelled';

export interface TravelFlightStatusResponse {
  itemId: string;
  airline: string | null;
  flightNumber: string;
  status: TravelFlightStatusValue;
  scheduledDeparture: string | null;
  actualDeparture: string | null;
  scheduledArrival: string | null;
  actualArrival: string | null;
  departureGate: string | null;
  arrivalGate: string | null;
  lastPolledAt: string | null;
  lastStatusChangeAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TravelFlightStatusFetchResponse {
  status: TravelFlightStatusResponse | null;
  /** True when the deployment has the feature on but the provider key is
   * missing — UI should render a "data unavailable" soft state. */
  providerUnavailable?: boolean;
  /** True when the provider had no matching flight for the query. */
  providerMiss?: boolean;
  /** Error message from the provider, when applicable. */
  providerError?: string;
}

export type TravelExpenseCategory = 'lodging' | 'food' | 'transport' | 'activity' | 'other';

export interface TravelExpenseSplitParticipantResponse {
  name: string;
  share: number;
}

export interface TravelExpenseSplitResponse {
  participants?: TravelExpenseSplitParticipantResponse[];
  paidBy?: string;
}

export interface TravelBudgetResponse {
  tripId: string;
  homeCurrency: string;
  totalCents: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface TravelExpenseResponse {
  expenseId: string;
  tripId: string;
  itemId: string | null;
  eventId: string | null;
  category: TravelExpenseCategory;
  description: string | null;
  amountCents: number;
  currency: string;
  amountHomeCents: number;
  fxRate: number;
  incurredAt: string;
  /**
   * Machine-generation tag: 'estimate' (budget estimator) or 'fare_scout'
   * (flight fare scout); null for human-entered rows. Tagged rows are
   * replaced wholesale by the next refresh of their generator.
   */
  source: string | null;
  paidBy: string | null;
  split: TravelExpenseSplitResponse;
  createdAt: string;
  updatedAt: string;
}

export interface TravelBudgetSummaryResponse {
  tripId: string;
  homeCurrency: string;
  totalCents: number | null;
  spentHomeCents: number;
  remainingHomeCents: number | null;
  expenseCount: number;
  perCategory: Array<{
    category: TravelExpenseCategory;
    amountHomeCents: number;
    count: number;
  }>;
  perDay: Array<{ date: string; amountHomeCents: number }>;
  perPerson: Array<{ name: string; amountHomeCents: number }>;
}

export type TravelDossierStatus = 'pending' | 'ready' | 'error';

export interface TravelDossierSource {
  url: string;
  title?: string;
  citedText?: string;
}

export interface TravelDossierResponse {
  dossierId: string;
  tripId: string;
  status: TravelDossierStatus;
  markdown: string | null;
  sources: TravelDossierSource[];
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

// ==========================================================================
// Travel Planner API methods
// ==========================================================================

export const travelApi = {
  getMyTravelTrips(options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit) qs.set('limit', String(options.limit));
    if (options?.offset) qs.set('offset', String(options.offset));
    const q = qs.toString();
    return api.httpRequest<TravelTripListResponse>('GET', `/arches/travel${q ? `?${q}` : ''}`);
  },

  getTravelTripDetail(tripId: string) {
    return api.httpRequest<TravelTripDetailResponse>('GET', `/arches/travel/${tripId}`);
  },

  createTravelTrip(input: {
    title?: string;
    startDate?: string;
    initialDays?: Array<{
      dayTitle?: string;
      city?: string;
      country?: string;
      accommodationName?: string;
      accommodationAddress?: string;
      accommodationCheckin?: string;
      accommodationCheckout?: string;
      events?: Array<{
        title: string;
        time?: string;
        eventType?: TravelEventType;
        notes?: string;
      }>;
    }>;
    initialTransits?: Array<{
      transitFrom?: string;
      transitTo?: string;
      transitMode: TravelTransitMode;
      transitDeparture?: string;
      transitArrival?: string;
      transitNotes?: string;
    }>;
  }) {
    // Server returns the full populated detail so the client can seed the
    // detail-query cache and the destination page renders immediately
    // (no second round-trip after navigation).
    return api.httpRequest<
      TravelTripDetailResponse & {
        skippedCommands?: Array<{ tool: string; reason: string }>;
      }
    >('POST', '/arches/travel', input);
  },

  updateTravelTrip(tripId: string, input: { title?: string; startDate?: string; status?: string }) {
    return api.httpRequest<{ trip: TravelTripResponse }>(
      'PATCH',
      `/arches/travel/${tripId}`,
      input,
    );
  },

  deleteTravelTrip(tripId: string) {
    return api.httpRequest<void>('DELETE', `/arches/travel/${tripId}`);
  },

  createTravelDay(tripId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ item: TravelItemResponse }>(
      'POST',
      `/arches/travel/${tripId}/days`,
      input,
    );
  },

  createTravelTransit(tripId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ item: TravelItemResponse }>(
      'POST',
      `/arches/travel/${tripId}/transit`,
      input,
    );
  },

  updateTravelItem(itemId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ item: TravelItemResponse }>(
      'PATCH',
      `/arches/travel/items/${itemId}`,
      input,
    );
  },

  deleteTravelItem(itemId: string) {
    return api.httpRequest<void>('DELETE', `/arches/travel/items/${itemId}`);
  },

  reorderTravelItems(tripId: string, itemIds: string[]) {
    return api.httpRequest<{ items: TravelItemResponse[] }>(
      'PUT',
      `/arches/travel/${tripId}/items/reorder`,
      { itemIds },
    );
  },

  createTravelEvent(itemId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ event: TravelEventResponse }>(
      'POST',
      `/arches/travel/items/${itemId}/events`,
      input,
    );
  },

  updateTravelEvent(eventId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ event: TravelEventResponse }>(
      'PATCH',
      `/arches/travel/events/${eventId}`,
      input,
    );
  },

  deleteTravelEvent(eventId: string) {
    return api.httpRequest<void>('DELETE', `/arches/travel/events/${eventId}`);
  },

  duplicateTravelDay(itemId: string) {
    return api.httpRequest<{ item: TravelItemResponse }>(
      'POST',
      `/arches/travel/items/${itemId}/duplicate`,
    );
  },

  reorderTravelEvents(itemId: string, eventIds: string[]) {
    return api.httpRequest<{ events: TravelEventResponse[] }>(
      'PUT',
      `/arches/travel/items/${itemId}/events/reorder`,
      { eventIds },
    );
  },

  moveTravelEvent(eventId: string, targetItemId: string) {
    return api.httpRequest<{ event: TravelEventResponse }>(
      'POST',
      `/arches/travel/events/${eventId}/move`,
      { targetItemId },
    );
  },

  duplicateTravelEvent(eventId: string, targetItemId: string) {
    return api.httpRequest<{ event: TravelEventResponse }>(
      'POST',
      `/arches/travel/events/${eventId}/duplicate`,
      { targetItemId },
    );
  },

  importTravelTrip(tripId: string, data: Record<string, unknown>) {
    return api.httpRequest<TravelTripDetailResponse>(
      'PUT',
      `/arches/travel/${tripId}/import`,
      data,
    );
  },

  // ─── Attachments ─────────────────────────────────────────────────────────
  // Backend removed the bespoke travel_attachments routes in W7;
  // document attachments now live in vault_document_references. The
  // event-level attach now routes through the Vault Documents Entity's
  // attach_to verb. The day/item-level attach and the list-by-trip
  // view are deferred — both need a new vault_document_references
  // "list by regarding" endpoint that doesn't yet exist server-side.

  async addTravelEventAttachment(eventId: string, documentId: string, _label?: string) {
    const result = await api.attachVaultDocument(documentId, {
      regardingType: 'travel_event',
      regardingId: eventId,
    });
    // Shape the Vault reference into the legacy TravelAttachmentResponse
    // so consumers don't have to be rewritten at the same time. The
    // `attachmentId` slot now carries the Vault referenceId; downstream
    // detach calls pass it back through removeTravelAttachment which
    // forwards to detachVaultDocumentReference.
    return {
      attachment: {
        attachmentId: result.reference.referenceId,
        tripId: '',
        itemId: null,
        eventId,
        fileId: documentId,
        label: null,
        displayOrder: 0,
        createdAt: result.reference.attachedAt,
        fileName: undefined,
        fileSize: undefined,
        mimeType: undefined,
      } satisfies TravelAttachmentResponse,
    };
  },

  // Day/item-level attachments are deferred. Calls throw an explicit
  // error so the UI surfaces the missing capability instead of 404'ing.
  addTravelItemAttachment(_itemId: string, _documentId: string, _label?: string) {
    return Promise.reject(
      new Error(
        'Day-level attachments are temporarily unavailable while the Vault attachment substrate is wired up. Attach to a specific event instead.',
      ),
    );
  },

  async removeTravelAttachment(referenceId: string, documentId: string) {
    await api.detachVaultDocumentReference(documentId, referenceId);
  },

  // --- Traveler Profile + Feature Flags ---

  getTravelUserProfile() {
    return api.httpRequest<TravelUserProfileResponse>('GET', '/arches/travel/profile');
  },

  updateTravelUserProfile(patch: Partial<TravelerProfileFields>) {
    return api.httpRequest<{ profile: TravelerProfileFields; updatedAt: string }>(
      'PATCH',
      '/arches/travel/profile',
      patch,
    );
  },

  getTravelFeatures() {
    return api.httpRequest<{ features: Record<string, boolean> }>('GET', '/arches/travel/features');
  },

  setTravelFeature(key: string, enabled: boolean) {
    return api.httpRequest<{ features: Record<string, boolean> }>(
      'PATCH',
      `/arches/travel/features/${encodeURIComponent(key)}`,
      { enabled },
    );
  },

  geocodeTravelItem(itemId: string, query: string) {
    return api.httpRequest<{ item: TravelItemResponse; result: TravelGeocodeResult }>(
      'POST',
      `/arches/travel/items/${itemId}/geocode`,
      { query },
    );
  },

  geocodeTravelEvent(eventId: string, query: string) {
    return api.httpRequest<{ event: TravelEventResponse; result: TravelGeocodeResult }>(
      'POST',
      `/arches/travel/events/${eventId}/geocode`,
      { query },
    );
  },

  generateTripDossier(tripId: string) {
    return api.httpRequest<{ dossier: TravelDossierResponse }>(
      'POST',
      `/arches/travel/${tripId}/dossier`,
    );
  },

  getLatestTripDossier(tripId: string) {
    return api.httpRequest<{ dossier: TravelDossierResponse | null }>(
      'GET',
      `/arches/travel/${tripId}/dossier`,
    );
  },

  // --- Email-Forward Inbox ---

  listTravelInboundEmails(options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit != null) qs.append('limit', String(options.limit));
    if (options?.offset != null) qs.append('offset', String(options.offset));
    const q = qs.toString();
    return api.httpRequest<TravelInboxListResponse>(
      'GET',
      `/arches/travel/inbox${q ? `?${q}` : ''}`,
    );
  },

  assignTravelInboundEmailToTrip(emailId: string, tripId: string) {
    return api.httpRequest<{ email: TravelInboundEmailResponse }>(
      'POST',
      `/arches/travel/inbox/${emailId}/assign-to-trip`,
      { tripId },
    );
  },

  reparseTravelInboundEmail(emailId: string) {
    return api.httpRequest<{ email: TravelInboundEmailResponse }>(
      'POST',
      `/arches/travel/inbox/${emailId}/reparse`,
    );
  },

  // --- Flight Status ---

  getTravelFlightStatus(itemId: string, options?: { force?: boolean }) {
    const qs = options?.force ? '?force=true' : '';
    return api.httpRequest<TravelFlightStatusFetchResponse>(
      'GET',
      `/arches/travel/items/${itemId}/flight-status${qs}`,
    );
  },

  // --- Budget + Expenses ---

  getTravelBudget(tripId: string) {
    return api.httpRequest<{ budget: TravelBudgetResponse | null }>(
      'GET',
      `/arches/travel/${tripId}/budget`,
    );
  },

  updateTravelBudget(tripId: string, input: { homeCurrency?: string; totalCents?: number | null }) {
    return api.httpRequest<{ budget: TravelBudgetResponse }>(
      'PATCH',
      `/arches/travel/${tripId}/budget`,
      input,
    );
  },

  getTravelBudgetSummary(tripId: string) {
    return api.httpRequest<{ summary: TravelBudgetSummaryResponse }>(
      'GET',
      `/arches/travel/${tripId}/budget/summary`,
    );
  },

  /** One-click autogenerate / refresh: AI-estimates the whole budget
   * (web-search-grounded) including flights. Replaces prior machine estimates;
   * keeps manually-entered expenses. */
  estimateTravelBudget(
    tripId: string,
    input: {
      cabin?: 'economy' | 'premium_economy' | 'business' | 'first';
      travelers?: number;
    } = {},
  ) {
    return api.httpRequest<{
      summary: TravelBudgetSummaryResponse;
      estimatedCount: number;
      flightCount: number;
      message?: string;
    }>('POST', `/arches/travel/${tripId}/budget/estimate`, input);
  },

  listTravelExpenses(tripId: string, options?: { limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (options?.limit != null) qs.append('limit', String(options.limit));
    if (options?.offset != null) qs.append('offset', String(options.offset));
    const q = qs.toString();
    return api.httpRequest<{ expenses: TravelExpenseResponse[]; total: number }>(
      'GET',
      `/arches/travel/${tripId}/expenses${q ? `?${q}` : ''}`,
    );
  },

  createTravelExpense(tripId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ expense: TravelExpenseResponse }>(
      'POST',
      `/arches/travel/${tripId}/expenses`,
      input,
    );
  },

  updateTravelExpense(expenseId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ expense: TravelExpenseResponse }>(
      'PATCH',
      `/arches/travel/expenses/${expenseId}`,
      input,
    );
  },

  deleteTravelExpense(expenseId: string) {
    return api.httpRequest<void>('DELETE', `/arches/travel/expenses/${expenseId}`);
  },

  // --- Packing list / todos ---

  listTravelTodos(tripId: string, options?: { category?: string }) {
    const qs = new URLSearchParams();
    if (options?.category) qs.append('category', options.category);
    const q = qs.toString();
    return api.httpRequest<{ todos: TravelTodoResponse[] }>(
      'GET',
      `/arches/travel/${tripId}/todos${q ? `?${q}` : ''}`,
    );
  },

  createTravelTodo(
    tripId: string,
    input: { title: string; notes?: string | null; category?: string },
  ) {
    return api.httpRequest<{ todo: TravelTodoResponse }>(
      'POST',
      `/arches/travel/${tripId}/todos`,
      input,
    );
  },

  updateTravelTodo(todoId: string, input: Record<string, unknown>) {
    return api.httpRequest<{ todo: TravelTodoResponse }>(
      'PATCH',
      `/arches/travel/todos/${todoId}`,
      input,
    );
  },

  deleteTravelTodo(todoId: string) {
    return api.httpRequest<void>('DELETE', `/arches/travel/todos/${todoId}`);
  },

  generateTravelPackingList(tripId: string, opts: { replace?: boolean } = {}) {
    const q = opts.replace ? '?replace=true' : '';
    return api.httpRequest<{
      inserted: TravelTodoResponse[];
      skippedCount: number;
      skipped: string[];
    }>('POST', `/arches/travel/${tripId}/packing-list${q}`);
  },

  // --- Reservation scout ---

  runTravelReservationScout(
    tripId: string,
    input: { scope?: 'trip' | 'day' | 'event'; itemId?: string; eventId?: string } = {},
  ) {
    return api.httpRequest<{ updated: TravelEventResponse[]; message?: string }>(
      'POST',
      `/arches/travel/${tripId}/reservation-scout`,
      input,
    );
  },

  // --- Trip info (per-trip context) ---

  getTravelTripInfo(tripId: string) {
    return api.httpRequest<{ info: TravelTripInfoResponse | null }>(
      'GET',
      `/arches/travel/${tripId}/info`,
    );
  },

  updateTravelTripInfo(tripId: string, input: UpdateTravelTripInfoInput) {
    return api.httpRequest<{ info: TravelTripInfoResponse }>(
      'PATCH',
      `/arches/travel/${tripId}/info`,
      input as unknown as Record<string, unknown>,
    );
  },

  // --- Reservation suggestions (discovery) ---

  listReservationSuggestions(tripId: string) {
    return api.httpRequest<{ suggestions: TravelReservationSuggestionResponse[] }>(
      'GET',
      `/arches/travel/${tripId}/reservation-suggestions`,
    );
  },

  discoverReservationSuggestions(tripId: string) {
    return api.httpRequest<{
      suggestions: TravelReservationSuggestionResponse[];
      addedCount: number;
      skippedCount: number;
      message?: string;
    }>('POST', `/arches/travel/${tripId}/reservation-suggestions/discover`);
  },

  updateReservationSuggestionStatus(
    suggestionId: string,
    status: TravelReservationSuggestionStatus,
  ) {
    return api.httpRequest<{ suggestion: TravelReservationSuggestionResponse }>(
      'PATCH',
      `/arches/travel/reservation-suggestions/${suggestionId}`,
      { status },
    );
  },

  deleteReservationSuggestion(suggestionId: string) {
    return api.httpRequest<void>(
      'DELETE',
      `/arches/travel/reservation-suggestions/${suggestionId}`,
    );
  },

  // --- Flight fare scout ---

  runTravelFlightFareScout(
    tripId: string,
    input: {
      cabin?: 'economy' | 'premium_economy' | 'business' | 'first';
      travelers?: number;
    } = {},
  ) {
    return api.httpRequest<{ updated: TravelExpenseResponse[]; message?: string }>(
      'POST',
      `/arches/travel/${tripId}/flight-fares`,
      input,
    );
  },

  // --- Share Links ---

  createTravelShareLink(tripId: string, input: { expiresAt?: string | null } = {}) {
    return api.httpRequest<{ shareLink: TravelShareLinkResponse }>(
      'POST',
      `/arches/travel/${tripId}/share`,
      input,
    );
  },

  revokeTravelShareLink(tripId: string, shareId: string) {
    return api.httpRequest<{ shareLink: TravelShareLinkResponse }>(
      'DELETE',
      `/arches/travel/${tripId}/share/${shareId}`,
    );
  },

  listTravelShareLinks(tripId: string) {
    return api.httpRequest<{ shareLinks: TravelShareLinkResponse[] }>(
      'GET',
      `/arches/travel/${tripId}/share`,
    );
  },

  listAllTravelShareLinks() {
    return api.httpRequest<{ shareLinks: TravelShareLinkWithTripResponse[] }>(
      'GET',
      `/arches/travel/share/all`,
    );
  },

  /**
   * Public, unauthenticated read. Called from the /shared/travel/:shareId
   * page to hydrate the read-only view. No auth headers are attached by
   * `request()` — the path is under /public/* which the server allows.
   */
  getSharedTravelTrip(shareId: string) {
    return api.httpRequest<TravelSharedTripResponse>('GET', `/public/travel/shared/${shareId}`);
  },

  // --- Trip critic ---

  runTravelCritic(tripId: string) {
    return api.httpRequest<{
      sections: Record<string, string>;
      modelUsed: string;
      signals: TravelCriticSignalsResponse;
    }>('POST', `/arches/travel/${tripId}/critic`);
  },
};
